import React from 'react';
import { motion } from 'framer-motion';
import { Globe, ShoppingCart, CheckCircle2, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const packages = [
  {
    id: 'wordpress-express',
    name: 'WordPress Express',
    price: '99.950 kr.',
    description: 'Staðlað útlit hannað af Vefsíðu',
    features: [
      'Staðlað WordPress þema',
      'Grunnstilling og uppsetning',
      'Tengingar við samfélagsmiðla',
      'SSL vottun',
      '5 síður innifaldar'
    ]
  },
  {
    id: 'woocommerce-express',
    name: 'WooCommerce Express',
    price: '149.950 kr.',
    description: 'Staðlað netverslunarútlit',
    features: [
      'Staðlað WooCommerce þema',
      'Grunnstilling og uppsetning',
      'Greiðslugáttir',
      'Vörustjórnun',
      'SSL vottun',
      '10 síður innifaldar'
    ]
  }
];

const WordPressExpress = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <motion.div 
            className="text-center max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl font-bold text-gray-900 mb-6">
              WordPress Express - Hraðvirk veflausn
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              Komdu þínu fyrirtæki á netið á einfaldan og hagkvæman hátt með 
              WordPress Express lausninni okkar.
            </p>
            <motion.div
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <Link to="/lausnir/wordpress-express/panta">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="bg-[#EA295A] text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-[#d01d4c] transition-all shadow-lg shadow-[#EA295A]/20 hover:shadow-xl hover:shadow-[#EA295A]/30 flex items-center"
                >
                  Panta núna
                  <ArrowRight className="ml-2" />
                </motion.button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Features Section */}
      <motion.div 
        className="py-24 bg-white"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Express pakkar
            </h2>
            <p className="text-xl text-gray-600">
              Hagkvæmar og hraðvirkar lausnir fyrir þitt fyrirtæki
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {packages.map((pkg, index) => (
              <motion.div 
                key={index}
                className="bg-white rounded-xl p-8 shadow-sm"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5 }}
              >
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-[#EA295A]/10 mb-6">
                  {pkg.id === 'wordpress-express' ? (
                    <Globe className="w-6 h-6 text-[#EA295A]" />
                  ) : (
                    <ShoppingCart className="w-6 h-6 text-[#EA295A]" />
                  )}
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{pkg.name}</h3>
                <div className="text-3xl font-bold text-[#EA295A] mb-1">{pkg.price}</div>
                <p className="text-gray-500 text-sm mb-4">verð án VSK</p>
                <p className="text-gray-600 mb-6">{pkg.description}</p>
                <ul className="space-y-3 mb-8">
                  {pkg.features.map((feature, i) => (
                    <li key={i} className="flex items-center text-gray-600">
                      <CheckCircle2 className="w-5 h-5 text-[#EA295A] mr-2" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link to="/lausnir/wordpress-express/panta">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full bg-[#EA295A] text-white px-6 py-3 rounded-lg font-medium hover:bg-[#d01d4c] transition-all flex items-center justify-center"
                  >
                    Panta núna
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </motion.button>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Benefits Section */}
      <motion.div 
        className="py-24 bg-gray-50"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Af hverju velja WordPress Express?
            </h2>
            <p className="text-xl text-gray-600">
              Kostir þess að velja WordPress Express lausnina
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            <motion.div 
              className="bg-white rounded-xl p-8 shadow-sm"
              whileHover={{ y: -5 }}
            >
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Hagkvæm lausn
              </h3>
              <p className="text-gray-600">
                Sparaðu tíma og peninga með tilbúinni lausn sem er sérsniðin að 
                þínum þörfum.
              </p>
            </motion.div>

            <motion.div 
              className="bg-white rounded-xl p-8 shadow-sm"
              whileHover={{ y: -5 }}
            >
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Hröð uppsetning
              </h3>
              <p className="text-gray-600">
                Komdu þínu fyrirtæki á netið á skömmum tíma með faglegri 
                uppsetningu.
              </p>
            </motion.div>

            <motion.div 
              className="bg-white rounded-xl p-8 shadow-sm"
              whileHover={{ y: -5 }}
            >
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Notendavænt
              </h3>
              <p className="text-gray-600">
                Einfalt og þægilegt stjórnborð sem auðvelt er að læra á.
              </p>
            </motion.div>

            <motion.div 
              className="bg-white rounded-xl p-8 shadow-sm"
              whileHover={{ y: -5 }}
            >
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Fagleg þjónusta
              </h3>
              <p className="text-gray-600">
                Við sjáum um alla uppsetningu og veitum þér þá aðstoð sem þú þarft.
              </p>
            </motion.div>
          </div>
        </div>
      </motion.div>

      {/* CTA Section */}
      <motion.div 
        className="py-24 bg-white"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-[#EA295A] to-[#FF6B6B] rounded-2xl p-12 text-center">
            <motion.h2 
              className="text-3xl font-bold text-white mb-6"
            >
              Tilbúin að koma þínu fyrirtæki á netið?
            </motion.h2>
            <motion.p 
              className="text-xl text-white/90 mb-8 max-w-2xl mx-auto"
            >
              Pantaðu WordPress Express lausn í dag og komdu þínu fyrirtæki á 
              netið á einfaldan og hagkvæman hátt
            </motion.p>
            <motion.div>
              <Link to="/lausnir/wordpress-express/panta">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="bg-white text-[#EA295A] px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-50 transition-all shadow-lg"
                >
                  Panta WordPress Express
                </motion.button>
              </Link>
            </motion.div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default WordPressExpress;